/*
 * <fz-home-tabs>: the homepage's Bestsellers / New / Christmas tabs
 * (sections/fz-home-tabs, homepage restructure 2026-10-10).
 *
 * The WAI-ARIA APG tabs pattern with AUTOMATIC activation: a tab shows its
 * panel as soon as it is focused. Left and Right move to the previous and next
 * tab (wrapping), Home and End to the first and last; only the selected tab is
 * in the Tab order (roving tabindex), and Tab from it goes into the panel.
 *
 * Every panel is rendered by the server, the first one shown and the rest
 * `hidden`, so without this script the first tab's cards still sell and SHOP
 * ALL still links. Switching only toggles `hidden` below the tabs, so nothing
 * above them moves, and the newly shown row starts at its first card.
 *
 * In the theme editor, selecting a tab's block shows that tab.
 */
class FzHomeTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (!this.tabs.length) return;
    this.list = this.tabs[0].closest('[role="tablist"]');
    this.list.addEventListener("click", this);
    this.list.addEventListener("keydown", this);
    if (window.Shopify?.designMode) document.addEventListener("shopify:block:select", this);
  }

  disconnectedCallback() {
    this.list?.removeEventListener("click", this);
    this.list?.removeEventListener("keydown", this);
    document.removeEventListener("shopify:block:select", this);
  }

  handleEvent(event) {
    if (event.type === "click") {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab, false);
      return;
    }
    if (event.type === "keydown") {
      this.onKey(event);
      return;
    }
    if (event.type === "shopify:block:select") {
      const tab = this.tabs.find((t) => {
        const panel = this.panelOf(t);
        return panel && (panel === event.target || panel.contains(event.target));
      });
      if (tab) this.select(tab, false);
    }
  }

  onKey(event) {
    const current = this.tabs.indexOf(event.target.closest('[role="tab"]'));
    if (current < 0) return;
    const last = this.tabs.length - 1;
    let next = null;
    switch (event.key) {
      case "ArrowRight":
        next = current === last ? 0 : current + 1;
        break;
      case "ArrowLeft":
        next = current === 0 ? last : current - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(this.tabs[next], true);
  }

  panelOf(tab) {
    const id = tab.getAttribute("aria-controls");
    return id ? document.getElementById(id) : null;
  }

  select(tab, focus) {
    for (const t of this.tabs) {
      const on = t === tab;
      const wasOn = t.getAttribute("aria-selected") === "true";
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = this.panelOf(t);
      if (!panel) continue;
      panel.hidden = !on;
      if (on && !wasOn) {
        const row = panel.querySelector(".fz-home-cards__list");
        if (row) row.scrollLeft = 0;
      }
    }
    if (focus) tab.focus();
  }
}

if (!customElements.get("fz-home-tabs")) customElements.define("fz-home-tabs", FzHomeTabs);
